import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationExtras, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DynamicGiftDto, GameModes, MarketType } from '@nova/types';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';

import { APP_ROUTE } from 'app/app-routing.constant';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { DataTrackLabelEnum as TeachDataTrackLabelEnum } from 'app/shared/enums/data-track-label.enum';
import { SupportedNewsLinks } from 'app/shared/enums/supported-news-links.enum';
import { NewsItem } from 'app/shared/models/news-widget/news-item-model';
import { DynamicGiftsService } from 'app/shared/services/dynamic-gifts.service';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { fireConfettiBurst } from 'app/shared/utils/confetti.util';
import { StudentBtnSizeOption } from 'app/student/components/student-btn/student-btn-size-option';
import { StudentBtnTypeOption } from 'app/student/components/student-btn/student-btn-type-option';
import { GAME_SECTION_ROUTE } from 'app/student/game-section/game-section-routing.constants';
import { GameConnectionType } from 'app/student/game-section/models/game-library/game';
import { StudentSchoolTimeCheckerService } from 'app/student/services/time-checker/student-school-time-checker.service';

@UntilDestroy()
@Component({
  selector: 'kh-news-article-modal',
  templateUrl: './news-article-modal.component.html',
  styleUrls: ['./news-article-modal.component.less'],
  standalone: false // eslint-disable-line @angular-eslint/prefer-standalone
})
export class NewsArticleModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly analytics = inject(KhAnalyticsService);
  private readonly timeCheckerService = inject(StudentSchoolTimeCheckerService);
  private readonly dynamicGiftsService = inject(DynamicGiftsService);
  private readonly translationService = inject(TranslationService);

  public data!: NewsItem;
  public classId!: string;
  public callBack?: () => void;
  public isTransientUser = false;

  public btnSizes = StudentBtnSizeOption;
  public btnTypes = StudentBtnTypeOption;

  public giftInfo: DynamicGiftDto | null = null;
  public giftClaimed = false;
  public giftClaimLoading = false;
  public onGiftClaimed?: () => void;

  public newsGiftsText = '';
  public claimGiftText = '';

  @ViewChild('giftSection') private _giftSectionRef?: ElementRef<HTMLElement>;

  public supportedInternalLinks: string[] = [
    SupportedNewsLinks.LaunchBay,
    SupportedNewsLinks.EditBots,
    SupportedNewsLinks.AllDayMarket,
    SupportedNewsLinks.AfterSchoolMarket,
    SupportedNewsLinks.SeasonalMarket,
    SupportedNewsLinks.ClassRoomGarden,
    SupportedNewsLinks.GameOutpostRun,
    SupportedNewsLinks.GameZoneRush,
    SupportedNewsLinks.GameTerraforming,
    SupportedNewsLinks.GameSelectionScreen,
    SupportedNewsLinks.SolveTab,
    SupportedNewsLinks.Arcade,
    SupportedNewsLinks.Mathalon,
    SupportedNewsLinks.RequestAGameshow,
    SupportedNewsLinks.AskForMission
  ];

  public constructor() {
    this.newsGiftsText = this.translationService.instant('NEWS_ARTICLE_MODAL.NEWS_GIFTS');
    this.claimGiftText = this.translationService.instant('NEWS_ARTICLE_MODAL.CLAIM_GIFT');
  }

  // #region Lifecycle

  public ngOnInit(): void {
    this.translationService.translationsReady$
      .pipe(untilDestroyed(this))
      .subscribe((translateService) => {
        this.newsGiftsText = String(translateService.instant('NEWS_ARTICLE_MODAL.NEWS_GIFTS'));
        this.claimGiftText = String(translateService.instant('NEWS_ARTICLE_MODAL.CLAIM_GIFT'));
      });
  }

  public ngAfterViewInit(): void {
    this.callBack?.();
  }

  public ngOnDestroy(): void {
    this.analytics.trackEvent({
      eventType: TeachDataTrackLabelEnum.StudentArticleFullModalDismissed,
      classId: this.classId,
      jsonData: {
        articleId: this.data.id,
        subjectline: this.data.subject,
        giftStatus: this.giftClaimed ? 'claimed' : this.giftInfo ? 'unclaimed' : 'no_gift'
      }
    });
  }

  // #endregion

  // #region Gift

  public claimGift(): void {
    if (!this.data?.id || this.giftClaimed || this.giftClaimLoading) { return; }

    this.giftClaimLoading = true;
    this.dynamicGiftsService.claimGift(this.data.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.onClaimGiftResponse({ trackAnalytics: true, showConfetti: true });
        },
        error: (err: unknown) => {
          // Already claimed server-side treat as success
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.onClaimGiftResponse({ trackAnalytics: false, showConfetti: false });
            return;
          }
          this.giftClaimLoading = false;
        }
      });
  }

  private onClaimGiftResponse(options: { trackAnalytics: boolean; showConfetti: boolean }): void {
    this.giftClaimed = true;
    this.giftClaimLoading = false;
    if (options.showConfetti) {
      this.fireGiftConfetti();
    }
    this.onGiftClaimed?.();
    if (options.trackAnalytics) {
      this.analytics.trackEvent({
        eventType: TeachDataTrackLabelEnum.StudentGiftClaimed,
        classId: this.classId,
        jsonData: {
          giftItemID: this.giftInfo?.item.code,
          articleId: this.data.id,
          subjectline: this.data.subject
        }
      });
    }
  }

  private fireGiftConfetti(): void {
    const el = this._giftSectionRef?.nativeElement;
    if (!el) { return; }

    const rect = el.getBoundingClientRect();
    fireConfettiBurst({
      originX: (rect.left + rect.width / 2) / window.innerWidth,
      originY: (rect.top + rect.height / 2) / window.innerHeight
    });
  }

  // #endregion

  // #region UI helpers

  public closeModal(): void {
    this.bsModalRef.hide();
  }

  public transformDescription(html: string): SafeHtml {
    const transformed = html.replace(/<br\s*\/?>/gi, '<div style="height:2rem;"></div>');
    return this.sanitizer.bypassSecurityTrustHtml(transformed);
  }

  // #endregion

  // #region Navigation

  public async goto(): Promise<void> {
    this.analytics.trackEvent({
      eventType: TeachDataTrackLabelEnum.StudentHyperlinkBttnClicked,
      classId: this.classId,
      jsonData: {
        articleId: this.data.id,
        subject: this.data.subject
      }
    });

    if (!this.data.hyperlink) {
      return;
    }

    if (this.supportedInternalLinks.includes(this.data.hyperlink)) {
      await this.goToInternalRoute();
      return;
    }

    // External link
    window.location.href = this.data.hyperlink;
    this.closeModal();
  }

  public async goToInternalRoute(): Promise<void> {
    const link = this.data.hyperlink as SupportedNewsLinks;

    switch (link) {
      // ===== Game Section routes (no blocker) =====
      case SupportedNewsLinks.LaunchBay:
        await this.navigateAndClose(GAME_SECTION_ROUTE.MAIN.route(this.classId));
        break;

      case SupportedNewsLinks.EditBots:
        await this.navigateAndClose(GAME_SECTION_ROUTE.EDIT_BOTS.route(this.classId));
        break;

      case SupportedNewsLinks.AllDayMarket:
        await this.navigateAndClose(
          GAME_SECTION_ROUTE.MARKET.route(this.classId),
          { queryParams: { marketType: MarketType.AllDayMarket } }
        );
        break;

      case SupportedNewsLinks.AfterSchoolMarket:
        await this.navigateAndClose(
          GAME_SECTION_ROUTE.MARKET.route(this.classId),
          { queryParams: { marketType: MarketType.AfterSchoolMarket } }
        );
        break;

      case SupportedNewsLinks.SeasonalMarket:
        await this.navigateAndClose(
          GAME_SECTION_ROUTE.MARKET.route(this.classId),
          { queryParams: { marketType: MarketType.SeasonalMarket } }
        );
        break;

      case SupportedNewsLinks.ClassRoomGarden:
        await this.navigateAndClose(
          GAME_SECTION_ROUTE.CLASSROOM_GARDEN.route(this.classId)
        );
        break;

      // ===== Restricted by school-hours / blocker logic =====
      case SupportedNewsLinks.GameOutpostRun:
        await this.navigateWithSchoolHoursCheck(
          APP_ROUTE.NOVA_GAME_OR.route(),
          {
            queryParams: {
              classId: this.classId,
              game: GameModes.OutpostRun,
              ct: GameConnectionType.SP
            }
          }
        );
        break;

      case SupportedNewsLinks.GameZoneRush:
        await this.navigateWithSchoolHoursCheck(
          APP_ROUTE.NOVA_GAME_ZR.route(),
          {
            queryParams: {
              classId: this.classId,
              game: GameModes.ZoneRush,
              ct: GameConnectionType.SP
            }
          }
        );
        break;

      // Terraforming is multiplayer-only and starts from the game selection screen.
      case SupportedNewsLinks.GameTerraforming:
      case SupportedNewsLinks.GameSelectionScreen:
        await this.navigateWithSchoolHoursCheck(
          GAME_SECTION_ROUTE.GAME_SELECT.route(this.classId)
        );
        break;

      case SupportedNewsLinks.Arcade:
        // Arcade's route guards enforce school hours (and the Play After GameShow exception)
        await this.navigateAndClose(GAME_SECTION_ROUTE.ARCADE.route(this.classId));
        break;

      // ===== Other student routes =====
      case SupportedNewsLinks.SolveTab:
        await this.navigateAndClose(['/student', this.classId, 'solve']);
        break;

      case SupportedNewsLinks.Mathalon:
        await this.navigateAndClose(['/student', this.classId, 'mathalon']);
        break;

      // Unmapped internal links for now
      case SupportedNewsLinks.RequestAGameshow:
      case SupportedNewsLinks.AskForMission:
      default:
        this.closeModal();
        break;
    }
  }

  /**
   * For restricted routes (Terraforming, OR, ZR, Game Select).
   * If isSchoolHours is true, send student to blocker page instead.
   */
  private async navigateWithSchoolHoursCheck(
    commands: unknown[],
    extras?: NavigationExtras
  ): Promise<void> {
    const { isSchoolHours } = await firstValueFrom(this.timeCheckerService.syncState());

    if (isSchoolHours) {
      await this.router.navigate(GAME_SECTION_ROUTE.BLOCKER_PAGE.route(this.classId));
      this.closeModal();
      return;
    }

    await this.router.navigate(commands, extras);
    this.closeModal();
  }

  /**
   * Simple navigate + close for non-restricted routes.
   */
  private async navigateAndClose(
    commands: unknown[],
    extras?: NavigationExtras
  ): Promise<void> {
    await this.router.navigate(commands, extras);
    this.closeModal();
  }

  // #endregion
}
